import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { API_CONFIG } from "../lib/constants/api";

interface DemoSettings {
	organizationName: string;
	logoUrl: string;
	language: string;
}

interface DemoSettingsContextType {
	settings: DemoSettings | null;
	logoUrl: string | null;
	organizationName: string | null;
}

const DemoSettingsContext = createContext<DemoSettingsContextType>({
	settings: null,
	logoUrl: null,
	organizationName: null,
});

export const useDemoSettings = () => useContext(DemoSettingsContext);

/**
 * `res.json()` is untyped, so the `string` fields on DemoSettings describe what
 * the API is supposed to send, not what it can send. A truthy non-string would
 * reach consumers and blow up on the first string method called on it — Baz
 * caught `organizationName?.trim()` in Communication.tsx doing exactly that,
 * crashing the whole Messages render instead of falling back. Normalising here
 * keeps every consumer safe rather than each one guarding separately.
 */
const asString = (value: unknown): string =>
	typeof value === "string" ? value : "";

const DEMO_CATALOG_SECRET =
	import.meta.env.VITE_DEMO_CATALOG_SECRET || "demo-catalog-2025";
const SETTINGS_MODE = import.meta.env.DEV ? "dev" : "prod";

export function DemoSettingsProvider({ children }: { children: ReactNode }) {
	const [settings, setSettings] = useState<DemoSettings | null>(null);
	const hasFetched = useRef(false);

	useEffect(() => {
		if (hasFetched.current) return;
		hasFetched.current = true;

		// Use standalone fetch to avoid the api client's 401 interceptor
		// which would trigger a login redirect if the secret is wrong/missing
		fetch(
			`${API_CONFIG.BASE_URL}/demo-catalog/settings?mode=${SETTINGS_MODE}`,
			{
				headers: { "x-demo-catalog-secret": DEMO_CATALOG_SECRET },
			},
		)
			.then((res) => {
				if (!res.ok) return null;
				return res.json();
			})
			.then((data) => {
				if (data?.success && data.data && !data.isDefault) {
					setSettings({
						organizationName: asString(data.data.organizationName),
						logoUrl: asString(data.data.logoUrl),
						language: asString(data.data.language),
					});
				}
			})
			.catch(() => {
				// Silently ignore — demo settings are optional
			});
	}, []);

	const logoUrl = settings?.logoUrl || null;
	const organizationName = settings?.organizationName || null;

	return (
		<DemoSettingsContext.Provider
			value={{ settings, logoUrl, organizationName }}
		>
			{children}
		</DemoSettingsContext.Provider>
	);
}
